<template>
  <div class="guest-membership-manager">
    <entity-settings-loading v-if="isLoading" />
    <template v-else>
      <!-- Add members -->
      <div class="entity-membership-manager__add-wrapper">
        <add-guest-form
          :entity-id-to-assign="entityId"
          :initial-rows="1"
          :max-rows="1"
          @account-memberships-created="getAssignPeopleInfo()"
        />
      </div>
      <!-- Companies list -->
      <div class="entity-membership-manager__companies-wrapper">
        <div v-for="company in companies" :key="company.id">
          <template v-if="hasEmployees(company)">
            <div class="company-header">
              <h4>{{ company.name }}</h4>
              <template v-if="hasMultipleEmployees(company)">
                <dd-button v-if="allEmployeesAreMembers(company)" class="all-button" type="text" @click="removeCompany(company)">
                  Remove everyone in this company
                </dd-button>
                <dd-button v-else class="all-button" type="text" @click="addCompany(company)">
                  Add everyone in this company
                </dd-button>
              </template>
            </div>
            <div v-for="person in company.listPeople" :key="person.id" class="roster-item">
              <avatar :person="person" :size="36" />
              <div class="person-info">
                <!-- Registered user -->
                <template v-if="person.hasRegistered">
                  <div class="headline">
                    <strong>{{ person.name }} <span v-if="$permissions.isLoggedInUser(person.id)">(You)</span></strong>
                  </div>
                  <div class="subline">{{ person.email }}</div>
                </template>
                <!-- Unregistered user -->
                <template v-else>
                  <div class="headline">
                    <strong>{{ person.name }}</strong>
                  </div>
                  <div class="subline">
                    Has not registered &mdash;
                    <span v-if="inviteSent(person.accountMembershipID)">
                      Invite sent!
                    </span>
                    <span v-else class="just-a-link" @click="sendSignInEmail(person.accountMembershipID)" >
                      Send invitation
                    </span>
                  </div>
                </template>
              </div>
              <add-remove-button
                :id="person.id"
                :is-selected="isMember(person.id)"
                :is-disabled="isSubmitting || isCurrentUser(person.id)"
                class="add-remove"
                @checked="(id) => addMembership(id)"
                @unchecked="(id) => removeMembership(id)"
              />            
            </div>
          </template>
        </div>
      </div>
    </template>
  </div>
</template>
<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import { debounce } from 'lodash-es'

/* Mixins */

/* Components */
import EntitySettingsLoading from '@/layouts/entity-settings-loading.vue'
import AddGuestForm from '@/screens/guest/components/add-guest-form.vue'

/* Helpers */

/* Data types */
import { EEntities } from '@/types'
import {
  IPersonBase,
  IProjectsOutputAssignPeopleCompany,
} from '@/api/types/response.types'

/* API */
import InternalProjectAPI from '@/api/modules/internal-projects.api'
import MailboxesAPI from '@/api/modules/mailboxes.api'
import AccountMembershipsAPI from '@/api/modules/account-memberships.api'

export default defineComponent({
  name: 'GuestMembershipManager',
  components: {
    EntitySettingsLoading,
    AddGuestForm,
  },
  props: {
    entityType: {
      type: String as () => EEntities,
      required: true,
    },
    entityId: {
      type: Number,
      required: true,
    },
    hideSuccessMessages: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['entity-name-fetched', 'memberships-updated'],
  data() {
    return {
      EEntities,
      isLoading: true,
      isSubmitting: false,
      debounceUpdateMemberships: null as any,
      sentInviteAccountMembershipIDs: [] as number[],
      companies: [] as IProjectsOutputAssignPeopleCompany[],
      memberIDs: [] as number[],
    }
  },
  computed: {
    isMailbox(): boolean {
      return this.entityType === EEntities.MAILBOXES
    },
    assignedPeople(): IPersonBase[] {
      const result: IPersonBase[] = []
      this.companies.forEach(c => {
        c.listPeople.filter(p => p.isAssigned).forEach(p => {
          result.push({
            id: p.id,
            name: p.name,
            photoUrl: p.photoUrl,
          })
        })
      })
      return result.sort((a, b) => (a.name > b.name ? 1 : -1))
    },
  },

  watch: {
    assignedPeople: {
      immediate: false,
      handler(val) {
        this.$emit('memberships-updated', val)
      },
    },
  },

  async created() {
    this.isLoading = true
    await this.getAssignPeopleInfo()
    this.debounceUpdateMemberships = debounce(this.updateMemberships, 500)
    this.isLoading = false
  },

  methods: {
    isMember(id: number): boolean {
      return this.memberIDs.includes(id)
    },
    hasEmployees(company: IProjectsOutputAssignPeopleCompany): boolean {
      return company.listPeople.length > 0
    },
    hasMultipleEmployees(company: IProjectsOutputAssignPeopleCompany): boolean {
      return company.listPeople.length > 1
    },
    allEmployeesAreMembers(
      company: IProjectsOutputAssignPeopleCompany,
    ): boolean {
      return company.listPeople.filter(p => !p.isAssigned).length === 0
    },
    inviteSent(accountMembershipID: number): boolean {
      return this.sentInviteAccountMembershipIDs.includes(accountMembershipID)
    },
    isCurrentUser(id: number): boolean {
      return this.$permissions.isLoggedInUser(id)
    },
    async getAssignPeopleInfo() {
      try {
        const response = await InternalProjectAPI.get_AssignGuests(
          this.entityId,
        )

        // Some implementers need the name of the entity (instead of doing its own separate call)...
        this.$emit('entity-name-fetched', response.name)
        this.companies = response.listCompanies
        this.memberIDs = this.assignedPeople.map(p => p.id)
      } catch (error) {
        this.$error.emit(error)
      }
    },
    async sendSignInEmail(accountMembershipID: number) {
      try {
        const response = await AccountMembershipsAPI.get_SendSignInEmail(
          accountMembershipID,
        )
        this.sentInviteAccountMembershipIDs.push(accountMembershipID)
        if (!this.hideSuccessMessages) {
          this.$messaging.showSuccess(response.message)
        }
      } catch (error) {
        this.$error.emit(error, true)
      }
    },
    async addMembership(id: number) {
      if (!this.memberIDs.includes(id)) {
        this.memberIDs.push(id)
        await this.debounceUpdateMemberships()
      }
    },
    async removeMembership(id: number) {
      // Should never happen since we disable this on the UI
      if (this.isCurrentUser(id)) {
        return
      }
      this.memberIDs = this.memberIDs.filter(m => m !== id)
      this.debounceUpdateMemberships()
    },
    async addCompany(company: IProjectsOutputAssignPeopleCompany) {
      company.listPeople.map(m => m.id).forEach(id => {
        if (!this.memberIDs.includes(id)) {
          this.memberIDs.push(id)
        }
      })
      // Don't debounce, just call immediately...
      await this.updateMemberships()
    },
    async removeCompany(company: IProjectsOutputAssignPeopleCompany) {
      company.listPeople.map(m => m.id).forEach(id => {
        if (this.memberIDs.includes(id) && !this.isCurrentUser(id)) {
          this.memberIDs = this.memberIDs.filter(m => m !== id)
        }
      })
      // Don't debounce, just call immediately...
      await this.updateMemberships()
    },
    async updateMemberships() {
      try {
        this.isSubmitting = true
        const response = this.isMailbox
          ? await MailboxesAPI.post_AssignPeople(this.entityId, this.memberIDs)
          : await InternalProjectAPI.post_AssignGuests(
              this.entityId,
              this.memberIDs,
            )
        if (!this.hideSuccessMessages) {
          this.$messaging.showSuccess(response.message)
        }
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isSubmitting = false
        await this.getAssignPeopleInfo()
      }
    },
  },
})
</script>

<style lang="scss">
.guest-membership-manager {
  overflow: visible;

  &__header {
    display: flex;
    justify-content: space-between;
  }

  &__companies-wrapper {
    width: calc(100% + 48px);
    height: 40vh;
    margin: 0 -24px;
    overflow-y: scroll;
    border-top: $border--solid-extra-light;
    border-bottom: $border--solid-extra-light;

    h4 {
      padding: 24px;
      padding-bottom: 0;
    }

    .company-header {
      display: flex;
      justify-content: space-between;
      position: relative;

      .all-button {
        top: 16px;
        right: 0;
        position: absolute;
      }
    }
  }

  &__add-wrapper {
    width: calc(100% + 48px);
    margin: 0 -24px;
    padding: 0px 24px;
    box-shadow: 0 8px 4px -4px rgba(0, 0, 0, 0.08) !important;
    border-bottom: $border--solid-extra-light;
  }

  .roster-item {
    display: flex;
    align-items: center;
    padding: 8px 20px;
    border-bottom: $border--solid-extra-light;
    @include lettering--p;

    .person-info {
      padding-left: 10px;
      line-height: 20px;

      .headline {
        margin-bottom: 1px;

        > a {
          color: var(--color-text-secondary);
        }
      }

      .subline {
        @include lettering--p-sm--no-color;
        color: var(--color-text-third);
      }
    }

    .add-remove {
      margin-left: auto;
    }
  }
}
</style>
